"use client";

import type { ConfettiIntensity } from "@gettactic/defines/src/feature-announcements";
import {
  Calendar,
  Clock,
  FileText,
  MapPin,
  MessageCircle,
  Search,
  Settings,
  Shield,
  Sparkles,
  Users,
  Wifi,
  Zap
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface ConfettiExplosionProps {
  intensity?: ConfettiIntensity;
  onComplete?: () => void;
}

// Confetti colors matching the theme
const CONFETTI_COLORS = [
  "#4F48F7", // Purple
  "#656CFF", // Light purple
  "#C43DA0", // Pink
  "#00D579", // Green
  "#48A1F7", // Blue
  "#06B6D4", // Cyan
  "#F97316", // Orange
  "#FBBF24", // Yellow
  "#EC4899", // Pink
  "#8B5CF6" // Violet
];

// Icons to include in the explosion
const FEATURE_ICONS = [
  Sparkles,
  MessageCircle,
  MapPin,
  Calendar,
  Users,
  Search,
  Zap,
  FileText,
  Wifi,
  Clock,
  Shield,
  Settings
];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  width: number;
  height: number;
  color: string;
  type: "rect" | "circle" | "streamer";
  opacity: number;
  life: number;
  maxLife: number;
  // For streamers
  points?: { x: number; y: number }[];
  streamerLength?: number;
}

interface IconParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  opacity: number;
  life: number;
  maxLife: number;
  IconComponent: React.ElementType;
  color: string;
}

const getParticleCount = (intensity: ConfettiIntensity) => {
  const counts = {
    1: { confetti: 30, streamers: 4, icons: 2 },
    2: { confetti: 50, streamers: 6, icons: 4 },
    3: { confetti: 80, streamers: 10, icons: 6 },
    4: { confetti: 120, streamers: 14, icons: 8 },
    5: { confetti: 180, streamers: 20, icons: 12 }
  };
  return counts[intensity];
};

export const ConfettiExplosion: React.FC<ConfettiExplosionProps> = ({
  intensity = 3,
  onComplete
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const [iconParticles, setIconParticles] = useState<IconParticle[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size to window size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const counts = getParticleCount(intensity);

    // Initialize confetti particles
    const particles: Particle[] = [];

    // Regular confetti (rectangles and circles)
    for (let i = 0; i < counts.confetti; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 8 + Math.random() * 12;
      const isCircle = Math.random() > 0.6;

      particles.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed * (0.5 + Math.random()),
        vy: Math.sin(angle) * speed * (0.5 + Math.random()) - 2,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 20,
        width: isCircle ? 6 + Math.random() * 6 : 8 + Math.random() * 10,
        height: isCircle ? 6 + Math.random() * 6 : 4 + Math.random() * 6,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        type: isCircle ? "circle" : "rect",
        opacity: 1,
        life: 0,
        maxLife: 80 + Math.random() * 40
      });
    }

    // Streamers (ribbon-like particles)
    for (let i = 0; i < counts.streamers; i++) {
      const angle = (i / counts.streamers) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const speed = 6 + Math.random() * 8;

      particles.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        rotation: angle * (180 / Math.PI),
        rotationSpeed: (Math.random() - 0.5) * 5,
        width: 4,
        height: 0,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        type: "streamer",
        opacity: 1,
        life: 0,
        maxLife: 100 + Math.random() * 30,
        points: [{ x: centerX, y: centerY }],
        streamerLength: 15 + Math.random() * 20
      });
    }

    particlesRef.current = particles;

    // Initialize icon particles
    const icons: IconParticle[] = [];
    for (let i = 0; i < counts.icons; i++) {
      const angle = (i / counts.icons) * Math.PI * 2 + Math.random() * 0.3;
      const speed = 5 + Math.random() * 6;

      icons.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        rotation: 0,
        rotationSpeed: (Math.random() - 0.5) * 8,
        scale: 1.2 + Math.random() * 0.8,
        opacity: 1,
        life: 0,
        maxLife: 90 + Math.random() * 30,
        IconComponent: FEATURE_ICONS[Math.floor(Math.random() * FEATURE_ICONS.length)],
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]
      });
    }
    setIconParticles(icons);

    // Animation loop
    let lastTime = performance.now();
    const gravity = 0.15;
    const friction = 0.99;

    const animate = (currentTime: number) => {
      const deltaTime = Math.min((currentTime - lastTime) / 16.67, 2);
      lastTime = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let activeParticles = 0;

      for (const p of particlesRef.current) {
        if (p.life >= p.maxLife) continue;
        activeParticles++;

        p.life += deltaTime;
        p.vy += gravity * deltaTime;
        p.vx *= friction;
        p.vy *= friction;
        p.x += p.vx * deltaTime;
        p.y += p.vy * deltaTime;
        p.rotation += p.rotationSpeed * deltaTime;

        // Fade out in the last 30% of life
        const fadeStart = p.maxLife * 0.7;
        if (p.life > fadeStart) {
          p.opacity = 1 - (p.life - fadeStart) / (p.maxLife - fadeStart);
        }

        ctx.save();
        ctx.globalAlpha = p.opacity;

        if (p.type === "streamer") {
          // Update streamer points
          p.points!.unshift({ x: p.x, y: p.y });
          if (p.points!.length > p.streamerLength!) {
            p.points!.pop();
          }

          // Draw streamer as curved line
          if (p.points!.length > 1) {
            ctx.beginPath();
            ctx.moveTo(p.points![0].x, p.points![0].y);

            for (let i = 1; i < p.points!.length; i++) {
              const point = p.points![i];
              ctx.lineTo(point.x, point.y);
            }

            ctx.strokeStyle = p.color;
            ctx.lineWidth = p.width * (1 - p.life / p.maxLife * 0.5);
            ctx.lineCap = "round";
            ctx.stroke();
          }
        } else {
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);

          if (p.type === "circle") {
            ctx.beginPath();
            ctx.arc(0, 0, p.width / 2, 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.fill();
          } else {
            ctx.fillStyle = p.color;
            ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
          }
        }

        ctx.restore();
      }

      // Update icon particles
      setIconParticles((prev) =>
        prev.map((icon) => {
          if (icon.life >= icon.maxLife) return icon;

          const newIcon = { ...icon };
          newIcon.life += deltaTime;
          newIcon.vy += gravity * deltaTime;
          newIcon.vx *= friction;
          newIcon.vy *= friction;
          newIcon.x += newIcon.vx * deltaTime;
          newIcon.y += newIcon.vy * deltaTime;
          newIcon.rotation += newIcon.rotationSpeed * deltaTime;

          const fadeStart = newIcon.maxLife * 0.6;
          if (newIcon.life > fadeStart) {
            newIcon.opacity = 1 - (newIcon.life - fadeStart) / (newIcon.maxLife - fadeStart);
          }

          if (newIcon.life < newIcon.maxLife) {
            activeParticles++;
          }

          return newIcon;
        })
      );

      if (activeParticles > 0) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsComplete(true);
        onComplete?.();
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [intensity, onComplete]);

  if (isComplete) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[9999]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ width: "100%", height: "100%" }}
      />
      {/* Render icon particles as DOM elements for crisp rendering */}
      {iconParticles.map((icon, index) => {
        if (icon.life >= icon.maxLife || icon.opacity <= 0) return null;
        const IconComponent = icon.IconComponent;
        return (
          <div
            key={index}
            className="absolute"
            style={{
              left: icon.x,
              top: icon.y,
              transform: `translate(-50%, -50%) rotate(${icon.rotation}deg) scale(${icon.scale})`,
              opacity: icon.opacity
            }}
          >
            <div
              className="rounded-xl p-2"
              style={{ backgroundColor: `${icon.color}25` }}
            >
              <IconComponent
                size={32}
                style={{ color: icon.color }}
                strokeWidth={2}
              />
            </div>
          </div>
        );
      })}
    </div>,
    document.body
  );
};
